import { LoginSocial } from 'components/Blocks/LoginForm/LoginSocial';
import {
    ButtonsDividerStyled,
    ButtonsStyled,
    LoginLostPassLinkStyled,
} from 'components/Blocks/Popup/Login/Login.style';
import Button from 'components/Forms/Button/Button';
import Form from 'components/Forms/Form/Form';
import FormLine from 'components/Forms/Lib/FormLine/FormLine';
import FormLineError from 'components/Forms/Lib/FormLineError/FormLineError';
import TextInput from 'components/Forms/TextInput/TextInput';
import { useLoginForm, useLoginFormMeta } from 'components/Pages/Login/formMeta';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { blurInput } from 'helpers/forms/blurInput';
import { handleFormErrors } from 'helpers/forms/handleFormErrors';
import { useAuth } from 'hooks/auth/useAuth';
import useTranslation from 'next-translate/useTranslation';
import { Controller, FormProvider, SubmitHandler } from 'react-hook-form';
import { usePersistStore } from 'store/usePersistStore';
import { LoginFormProps } from 'types/login';
import { getInternationalizedStaticUrls } from 'utils/getInternationalizedStaticUrls';

export const LoginForm: FC<LoginFormProps> = ({ shouldOverwriteCustomerUserCart, defaultEmail }) => {
    const { t } = useTranslation();
    const { url } = useDomainConfig();
    const [resetPasswordUrl] = getInternationalizedStaticUrls(['/reset-password'], url);
    const { cartUuid } = usePersistStore((s) => s);
    const { login } = useAuth();
    const [formProviderMethods] = useLoginForm(defaultEmail);
    const {
        fields: { email: emailField, password: passwordField },
        formName,
    } = useLoginFormMeta(formProviderMethods);

    const onLoginHandler: SubmitHandler<{ email: string; password: string }> = async (data) => {
        blurInput();

        const loginResult = await login({
            email: data.email,
            password: data.password,
            previousCartUuid: cartUuid,
            shouldOverwriteCustomerUserCart: shouldOverwriteCustomerUserCart ?? false,
        });

        handleFormErrors(loginResult.error, formProviderMethods, t);
    };

    return (
        <>
            <h3 className="mb-2 text-h3 leading-6 vl:mb-[22px] vl:text-h2 vl:leading-8">{t('Log in')}</h3>
            <FormProvider {...formProviderMethods}>
                <Form name="login" onSubmit={formProviderMethods.handleSubmit(onLoginHandler)}>
                    <Controller
                        name={emailField.name}
                        render={({ fieldState: { isTouched, invalid, error }, field }) => (
                            <FormLine bottomGap>
                                <TextInput
                                    required
                                    fieldRef={field}
                                    hasError={invalid}
                                    id={`${formName}-${emailField.name}`}
                                    isTouched={isTouched}
                                    label={t('Email')}
                                    name={emailField.name}
                                    type="text"
                                />
                                <FormLineError
                                    data-testid={`${formName}-${emailField.name}-error`}
                                    error={error}
                                    inputType="text-input"
                                    textInputSize="small"
                                />
                            </FormLine>
                        )}
                    />
                    <Controller
                        name={passwordField.name}
                        render={({ fieldState: { isTouched, invalid, error }, field }) => (
                            <FormLine>
                                <TextInput
                                    required
                                    fieldRef={field}
                                    hasError={invalid}
                                    id={`${formName}-${passwordField.name}`}
                                    isTouched={isTouched}
                                    label={passwordField.label}
                                    name={passwordField.name}
                                    type="password"
                                />
                                <FormLineError
                                    data-testid={`${formName}-${passwordField.name}-error`}
                                    error={error}
                                    inputType="text-input-password"
                                    textInputSize="small"
                                />
                            </FormLine>
                        )}
                    />
                    <ButtonsStyled>
                        <Button data-testid="blocks-popup-login-submit" type="submit" variant="primary">
                            {t('Log-in')}
                        </Button>
                        <ButtonsDividerStyled />
                        <LoginLostPassLinkStyled href={resetPasswordUrl}>
                            {t('Lost your password?')}
                        </LoginLostPassLinkStyled>
                    </ButtonsStyled>
                    <LoginSocial
                        cartUuid={cartUuid}
                        shouldOverwriteCustomerUserCart={shouldOverwriteCustomerUserCart}
                    />
                </Form>
            </FormProvider>
        </>
    );
};
